"use client";

// arena-sprite.tsx
// Shared sprite types, the animated sprite component, and the sprite fetch utility.
// Used by live-arena.tsx, arena-chat-panel.tsx, and arena-overlays.tsx.

import { useEffect, useRef, useState } from "react";
import { clog } from "@/lib/client-log";
import { spriteFrameStyle } from "@/lib/sprite-loader";
import type { CustomSpriteEntry } from "@/lib/sprite-loader";

export interface SpriteAnimMeta {
  animated: boolean;
  frame_count?: number;
  frame_rate_ms?: number;
  frame_width?: number;
  sheet_width?: number;
  sheet_height?: number;
  frame_offsets?: number[];
}

export interface Spectator {
  userId: string;
  displayName: string;
  spriteClass: string;
  spritePalette: string;
  spriteUri: string;
  animMeta?: SpriteAnimMeta;
  away?: boolean;
}

export interface FloatingCheer {
  id: string;
  emoji: string;
  x: number;
  createdAt: number;
  displayName: string;
  spriteUri: string;
  animMeta?: SpriteAnimMeta;
}

// ── Animated sprite renderer ──────────────────────────────────────────────────
export function SpectatorSprite({ uri, animMeta, size, displayName }: {
  uri: string;
  animMeta?: SpriteAnimMeta;
  size: number;
  displayName: string;
}) {
  const frameCount = animMeta?.frame_count ?? 1;
  const rateMs = animMeta?.frame_rate_ms ?? 150;
  const [frame, setFrame] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!animMeta?.animated || frameCount <= 1) return;
    timerRef.current = setInterval(() => setFrame((f) => (f + 1) % frameCount), rateMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [animMeta?.animated, frameCount, rateMs]);

  if (!animMeta?.animated || frameCount <= 1) {
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={uri} alt={displayName} width={size} height={size} style={{ imageRendering: "pixelated" }} title={displayName} />;
  }

  const entry: CustomSpriteEntry = {
    id: "", label: displayName, source: "custom",
    file: uri.replace("/sprites/custom/", ""),
    palette_count: 1,
    frame_count: animMeta.frame_count,
    frame_rate_ms: animMeta.frame_rate_ms,
    frame_width: animMeta.frame_width,
    sheet_width: animMeta.sheet_width,
    sheet_height: animMeta.sheet_height,
    frame_offsets: animMeta.frame_offsets,
  };
  const { style: frameStyle, scaledFrameWidth } = spriteFrameStyle(entry, frame, size);
  return (
    <div style={{ width: size, height: size, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }} title={displayName}>
      <div style={{ ...frameStyle, marginLeft: scaledFrameWidth < size ? (size - scaledFrameWidth) / 2 : 0 }} />
    </div>
  );
}

// ── Sprite data fetcher ───────────────────────────────────────────────────────
export async function fetchSpriteData(
  userId: string,
  spriteClass: string,
  spritePalette: string,
): Promise<{ uri: string; animMeta?: SpriteAnimMeta }> {
  try {
    const params = new URLSearchParams({ userId, spriteClass, spritePalette, size: "32" });
    const res = await fetch(`/api/sprite?${params}`);
    const text = await res.text();
    if (!text.trim()) return { uri: "" };
    const data = JSON.parse(text);
    const uri = data.uri ?? "";
    const animMeta: SpriteAnimMeta | undefined = data.animated ? {
      animated: true,
      frame_count: data.frame_count,
      frame_rate_ms: data.frame_rate_ms,
      frame_width: data.frame_width,
      sheet_width: data.sheet_width,
      sheet_height: data.sheet_height,
      frame_offsets: data.frame_offsets,
    } : undefined;
    return { uri, animMeta };
  } catch (err) {
    clog.warn("arena", "fetchSpriteData failed", { userId, spriteClass, error: err instanceof Error ? err.message : String(err) });
    return { uri: "" };
  }
}
